import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Heading from '../../../components/Common/Heading';
import Guide from '../../../components/Common/Guide';
import Loader from '../../../components/Common/Loader';
import ShowValidationError from '../../../components/Common/ValidationError';
import { ProfileShippingAddressValues } from '../Profile';
import { getUSStates } from '../../../config/usa-states';
import { getCountries } from '../../../config/countries';
import { UserModel } from '../../../interfaces/user.interface';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

interface ShippingAddressFormValues {
  shippingName: string;
  recipientName: string;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  shippingCountry: string;
}

const shippingAddressSchema = Yup.object().shape({
  shippingName: Yup.string().required('Company name is required'),
  recipientName: Yup.string().required("Recipient's name is required"),
  shippingAddress1: Yup.string().required('Address is required'),
  shippingAddress2: Yup.string(),
  shippingCity: Yup.string().required('City is required'),
  shippingState: Yup.string().required('State is required'),
  shippingZip: Yup.string()
    .required('Zip code is required')
    .matches(/^\d{5}(-\d{4})?$/, 'Zip code must be in format 12345 or 12345-6789'),
  shippingCountry: Yup.string().required('Country is required'),
});

interface ProfileFormProps {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  values: {
    firstName: string;
    lastName: string;
    email: string;
    settings: Record<string, boolean | string | undefined>;
    shippingAddress?: ProfileShippingAddressValues;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors: Record<string, string>;
  loader: {
    profileUpdate: boolean;
  };
  user: UserModel | null;
}

// Create a context to track form submission attempts
const FormSubmitContext = createContext(false);

const ShippingAddressForm = () => {
  const {
    errors: shippingErrors,
    handleChange: shippingHandleChange,
    values: shippingValues,
  } = useFormikContext<ShippingAddressFormValues>();
  const [usaStates, setUsaStates] = useState<Array<string>>([]);
  const [countries, setCountries] = useState<Array<string>>([]);
  const formSubmitAttempted = useContext(FormSubmitContext);

  useEffect(() => {
    if (!usaStates?.length) {
      setUsaStates(getUSStates());
      setCountries(getCountries().filter((e) => e === 'United States'));
    }
  }, []);

  return (
    <div className="flex flex-col w-full md:w-[50%]">
      <h1 className="text-[#a1a1a1] text-md mt-6">Company name</h1>
      <div className="w-full">
        <input
          type="text"
          name="shippingName"
          value={shippingValues.shippingName}
          onChange={shippingHandleChange}
          placeholder="Company Name"
          className="p-2 w-full rounded-md"
        />
        <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingName || '' : ''} />
      </div>

      <h1 className="text-[#a1a1a1] text-md mt-2">Recipient's Name</h1>
      <div className="w-full">
        <input
          type="text"
          name="recipientName"
          value={shippingValues.recipientName}
          onChange={shippingHandleChange}
          placeholder="Recipient's Name"
          className="p-2 w-full rounded-md"
        />
        <ShowValidationError error={formSubmitAttempted ? shippingErrors.recipientName || '' : ''} />
      </div>

      <h1 className="text-[#a1a1a1] text-md mt-2">Shipping Address</h1>
      <div className="w-full">
        <input
          type="text"
          name="shippingAddress1"
          value={shippingValues.shippingAddress1}
          onChange={shippingHandleChange}
          placeholder="Address 1"
          className="p-2 w-full rounded-md"
        />
        <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingAddress1 || '' : ''} />
      </div>
      <div className="w-full">
        <input
          type="text"
          name="shippingAddress2"
          value={shippingValues.shippingAddress2}
          onChange={shippingHandleChange}
          placeholder="Address 2"
          className="p-2 w-full rounded-md"
        />
      </div>
      <div className="flex gap-7 mt-1">
        <div>
          <input
            type="text"
            name="shippingCity"
            value={shippingValues.shippingCity}
            onChange={shippingHandleChange}
            placeholder="City"
            className="p-2 w-full rounded-md"
          />
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingCity || '' : ''} />
        </div>
        <div>
          <select
            name="shippingState"
            value={shippingValues.shippingState}
            onChange={shippingHandleChange}
            className="p-[0.6rem] w-full rounded-md"
          >
            <option value="" disabled selected hidden>
              State
            </option>
            {usaStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingState || '' : ''} />
        </div>

        <div>
          <input
            type="text"
            name="shippingZip"
            value={shippingValues.shippingZip}
            onChange={shippingHandleChange}
            placeholder="Zip"
            className="p-2 w-full rounded-md"
          />
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingZip || '' : ''} />
        </div>
      </div>
      <div className="w-full flex flex-col items-start gap-2 mt-2">
        <div className="w-full">
          <select
            name="shippingCountry"
            value={shippingValues.shippingCountry}
            onChange={shippingHandleChange}
            className="p-2 w-full rounded-md"
          >
            {countries.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingCountry || '' : ''} />
        </div>
      </div>
    </div>
  );
};

const ProfileForm: React.FC<ProfileFormProps> = ({
  handleSubmit,
  values,
  handleChange,
  errors,
  loader,
  user,
}: ProfileFormProps) => {
  const formikRef = useRef<any>(null);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  // Helper function to create a synthetic event with preventDefault
  const createSyntheticEvent = (formData: any): React.FormEvent<HTMLFormElement> => {
    const syntheticEvent = {
      ...formData,
      preventDefault: () => {},
      target: {
        elements: {
          // Add any elements that might be accessed in handleSubmit
        },
      },
    } as unknown as React.FormEvent<HTMLFormElement>;
    return syntheticEvent;
  };

  const handleShippingAddressSubmit = (shippingValues: ShippingAddressFormValues) => {
    // Create a synthetic event with the combined values
    const formData = {
      ...values,
      shippingAddress: shippingValues,
    };

    // Call the parent handleSubmit with a synthetic event
    const syntheticEvent = createSyntheticEvent(formData);
    handleSubmit(syntheticEvent);
  };

  const getInitialShippingValues = (): ShippingAddressFormValues => {
    if (values.shippingAddress) {
      return {
        shippingName: values.shippingAddress.shippingName || '',
        recipientName: values.shippingAddress.recipientName || `${values.firstName} ${values.lastName}`.trim(),
        shippingAddress1: values.shippingAddress.shippingAddress1 || '',
        shippingAddress2: values.shippingAddress.shippingAddress2 || '',
        shippingCity: values.shippingAddress.shippingCity || '',
        shippingState: values.shippingAddress.shippingState || '',
        shippingZip: values.shippingAddress.shippingZip || '',
        shippingCountry: values.shippingAddress.shippingCountry || 'United States',
      };
    }
    return {
      shippingName: '',
      recipientName: `${values.firstName} ${values.lastName}`.trim(),
      shippingAddress1: '',
      shippingAddress2: '',
      shippingCity: '',
      shippingState: '',
      shippingZip: '',
      shippingCountry: 'United States',
    };
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Set that form submission was attempted
    setFormSubmitAttempted(true);

    // If we have a shipping address form and user has enabled saving shipping address
    if (formikRef.current && user?.saveDefaultShippingAddress && false) {
      // Validate the Formik form
      const errors = await formikRef.current.validateForm();

      // Mark all fields as touched to show validation errors
      const touchedFields: Record<string, boolean> = {};
      Object.keys(getInitialShippingValues()).forEach((key) => {
        touchedFields[key] = true;
      });
      formikRef.current.setTouched(touchedFields);

      // If there are validation errors, don't submit
      if (Object.keys(errors).length > 0) {
        console.log('Validation errors:', errors);
        return;
      }

      // Get the current values from Formik
      const shippingValues = formikRef.current.values;

      // Make sure all required fields have values
      const requiredFields = [
        'shippingName',
        'recipientName',
        'shippingAddress1',
        'shippingCity',
        'shippingState',
        'shippingZip',
        'shippingCountry',
      ];

      const missingFields = requiredFields.filter((field) => !shippingValues[field]);

      if (missingFields.length > 0) {
        console.log('Missing required fields:', missingFields);
        return;
      }

      // Create the combined form data
      const formData = {
        ...values,
        shippingAddress: shippingValues,
      };

      // Call the parent handleSubmit with a synthetic event
      const syntheticEvent = createSyntheticEvent(formData);
      handleSubmit(syntheticEvent);
    } else {
      // If no shipping form, just submit the main form with the original event
      handleSubmit(e);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center align-end mb-4">
        <Heading title="My Profile" />
        <div>
          <Guide />
        </div>
      </div>
      <form id="form-signup" name="form-signup" className="mt-8 text-start" onSubmit={handleFormSubmit}>
        <input
          type="text"
          name="firstName"
          value={values.firstName}
          onChange={handleChange}
          placeholder="First Name"
          className="mt-4 p-2 w-full rounded-md"
          autoFocus
        />
        <div className="text-red-600 mt-1">{errors.firstName}</div>
        <input
          type="text"
          name="lastName"
          value={values.lastName}
          onChange={handleChange}
          placeholder="Last Name"
          className="mt-4 p-2 w-full rounded-md"
        />
        <div className="text-red-600 mt-1">{errors.lastName}</div>
        <input
          type="email"
          name="email"
          value={values.email}
          onChange={handleChange}
          placeholder="Email"
          className="mt-4 p-2 w-full rounded-md text-[#9ea4b0]"
          readOnly
        />
        <div className="text-red-600 mt-1">{errors.email}</div>
        {values.settings &&
          Object.entries(values.settings)
            .filter(([key, value]) => !key?.toLowerCase()?.includes('promotional'))
            .map(([key, value]) => (
              <div key={key} className="w-full flex flex-col space-between">
                <label className="capitalize mt-2 pt-2 cursor-pointer">
                  {key.replace(/_/g, ' ').replace('USER', '').toLocaleLowerCase()}
                </label>
                <select
                  className="mt-4 p-2 w-[50%] md:w-[15%] cursor-pointer bg-white rounded-md"
                  id={key}
                  name={key}
                  onChange={handleChange}
                  value={value?.toString()}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            ))}

        {/*{user?.saveDefaultShippingAddress && (*/}
        {/*  <FormSubmitContext.Provider value={formSubmitAttempted}>*/}
        {/*    <Formik*/}
        {/*      initialValues={getInitialShippingValues()}*/}
        {/*      validationSchema={shippingAddressSchema}*/}
        {/*      onSubmit={handleShippingAddressSubmit}*/}
        {/*      enableReinitialize={true}*/}
        {/*      innerRef={formikRef}*/}
        {/*    >*/}
        {/*      <ShippingAddressForm />*/}
        {/*    </Formik>*/}
        {/*  </FormSubmitContext.Provider>*/}
        {/*)}*/}
        <div className="flex flex-wrap gap-2 md:flex-nowrap mt-5">
          <button
            type="submit"
            className="flex justify-center gap-2 bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-sm items-center"
            disabled={loader.profileUpdate}
          >
            Update Profile
            <Loader loader={loader.profileUpdate} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
